import { lazy, type ComponentType } from "react";

/*
 * After a deploy, a tab that was already open still points to the old code
 * files, which no longer exist: opening a page then fails with a ChunkLoadError.
 * lazyRetry loads the page like React.lazy and, on that error, reloads once to
 * pick up the new version. The time guard (sessionStorage) stops a reload loop
 * if the file is really missing.
 */
const KEY = "migrada_chunk_reload";
const CHUNK_ERROR = /ChunkLoadError|Loading chunk|Loading CSS chunk|dynamically imported module/i;

export function lazyRetry<T extends ComponentType<any>>(load: () => Promise<{ default: T }>) {
  return lazy(() =>
    load().catch((err: unknown) => {
      const e = err as { name?: string; message?: string } | null;
      let last = 0;
      try {
        last = Number(window.sessionStorage.getItem(KEY)) || 0;
      } catch {
        // storage blocked: still try one reload below
      }
      if (CHUNK_ERROR.test(`${e?.name ?? ""} ${e?.message ?? ""}`) && Date.now() - last > 10000) {
        try {
          window.sessionStorage.setItem(KEY, String(Date.now()));
        } catch {
          // ignore
        }
        window.location.reload();
        return new Promise<never>(() => undefined);
      }
      throw err;
    }),
  );
}
